// TODO: remove this file if this file replaces the default ImageCropper
/* istanbul ignore file */
import { useCallback } from 'react';
import type React from 'react';

import type Cropper from 'cropperjs';

import { Button, Text } from '@change/ds-core-ui';
import { CloseIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import type { MediaCropArea } from '#src/app/shared/hooks/imageUpload';
import type { UploaderControls } from '#src/app/shared/hooks/uppyUploader';

import { CropperElement } from './CropperElement';

type ImageCropperProps = {
	crop?: MediaCropArea;
	disabled?: boolean;
	/** Hides the floating remove button overlaid on the image, for callers that render their own
	 * remove control elsewhere. Dragging/repositioning stays enabled — unlike `disabled`, this only
	 * affects the button's visibility. */
	hideRemoveButton?: boolean;
	image?: string;
	onCancelClick?: () => void;
	onImageChange: (image?: string) => void;
	onImageCrop: (imageCrop?: MediaCropArea) => void;
	uploader: UploaderControls;
	onInitialized?: (instance: Cropper) => void;
	/** Rounds the image's top corners and the "Drag to reposition" hint box's bottom corners, so the
	 * two read as one seamless block — off by default to match every other consumer's existing
	 * square look. Each piece rounds its own corners rather than clipping both from an outer
	 * wrapper, since the hint box's own border would otherwise get clipped off right at the curve. */
	rounded?: boolean;
	/** Overrides the "Drag to reposition" hint box's classes, merged over the defaults — for callers
	 * whose surrounding background would otherwise blend with the box's default fill. */
	instructionsClassName?: string;
};

export const ImageCropper: React.FC<ImageCropperProps> = ({
	crop,
	disabled = false,
	hideRemoveButton = false,
	image,
	onImageChange,
	onCancelClick,
	onImageCrop,
	uploader,
	onInitialized,
	rounded = false,
	instructionsClassName,
}) => {
	const { translate } = useI18n();
	const onCancelReposition = useCallback(() => {
		const file = uploader.getFile();
		if (file) {
			uploader.removeFile(file.id);
		}
		onImageChange(undefined);
		onImageCrop(undefined);
		if (onCancelClick) onCancelClick();
	}, [onCancelClick, onImageChange, onImageCrop, uploader]);

	return (
		<div className="image-cropper">
			<div className={cn('relative aspect-video [overflow:inherit]', rounded && 'overflow-hidden rounded-t-lg')}>
				<CropperElement
					crop={crop}
					data-testid="cropper-element"
					disabled={disabled}
					image={image}
					updateCrop={onImageCrop}
					updateImage={uploader.updateImage}
					onInitialized={onInitialized}
				/>
				{!disabled && !hideRemoveButton && (
					<Button
						variant="secondary-solid"
						size="small"
						iconOnly
						// Button clones leadingIcon and force-overrides its `size` prop to "medium",
						// so shrinking the icon has to go through className/svgClassName instead.
						leadingIcon={<CloseIcon className="h-4 w-4" svgClassName="h-4 w-4" />}
						aria-label={translate('corgi.sap.media.a11y.cropper.button_aria_label')}
						data-testid="remove-img-btn"
						className="absolute top-0 right-0 z-[1] mt-4 mr-4"
						onClick={onCancelReposition}
					/>
				)}
			</div>
			{!disabled && (
				<div
					data-testid="cropper-instructions"
					className={cn(
						'border-stroke-weaker bg-background-raised mt-0 border border-solid p-2',
						rounded && 'rounded-b-lg',
						instructionsClassName,
					)}
				>
					<Text size="small">
						<Translate value="corgi.sap.media.cropper.reposition" />
					</Text>
				</div>
			)}
		</div>
	);
};
